import React, { useEffect } from 'react';
import * as Yup from 'yup';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import { Form } from '@dashboard/components/Formik';
import Link from '@dashboard/components/Link';
import getSellerDomain from '@/dashboard/services/getSellerDomain';

const validationSchema = Yup.object().shape({
  displayName: Yup.string().required('Store name is required'),
  brandColor: Yup.string(),
  defaultCurrency: Yup.string().required('Default currency is required'),
  url: Yup.string().url('Must be a valid URL'),
  homepage: Yup.object().shape({
    type: Yup.string().oneOf(['customerPortal', 'checkout', 'redirect', '']),
    checkout: Yup.string().when('type', {
      is: 'checkout',
      then: Yup.string().required('Checkout is required'),
    }),
    redirectUrl: Yup.string().when('type', {
      is: 'redirect',
      then: Yup.string().url().required('Redirect URL is required'),
    }),
  }),
});

const StoreForm = () => {
  const { seller } = useAuth();

  const { updateSeller } = useSellerQueries();

  useEffect(() => {
    const hash = window.location.hash;

    if (hash) {
      const element = document.getElementById(hash.substring(1));
      if (element) {
        element.scrollIntoView();
      }
    }
  }, []);

  return (
    <Form
      enableReinitialize
      validationSchema={validationSchema}
      initialValues={{
        displayName: seller.displayName,
        brandColor: seller.brandColor,
        logo: seller.logo,
        defaultCurrency: seller.defaultCurrency,
        homepage: seller.homepage,
        favicon: seller.favicon,
      }}
      fields={({ values }) => [
        {
          name: 'displayName',
          required: true,
          type: 'text',
          label: 'Store name',
        },
        {
          name: 'logo',
          required: false,
          type: 'image',
          label: 'Logo',
          image: seller.logo,
          crop: { height: 512, width: 512, aspect: 1 },
          previewSize: 'logo',
          onUpdate: async image => {
            await updateSeller({ logo: image });
          },
        },
        {
          name: 'brandColor',
          required: false,
          type: 'color-select',
          label: 'Brand color',
        },
        {
          type: 'divider',
        },
        {
          name: 'defaultCurrency',
          required: true,
          type: 'currency',
          label: 'Default currency',
        },
        {
          type: 'divider',
          id: 'homepage',
        },

        // homepage
        {
          name: 'homepage.type',
          required: false,
          type: 'select',
          label: 'Homepage',
          labelDescription: 'Display your customer portal, a page, or redirect to a URL.',
          options: [
            { value: 'customerPortal', label: 'Customer portal' },
            { value: 'checkout', label: 'Page' },
            { value: 'redirect', label: 'Redirect to URL' },
            { value: '', label: 'No homepage (blank)' },
          ],
          additionalContent: (
            <div className="mt-2 text-sm">
              <Link href={getSellerDomain(seller)} target="_blank" external>
                View homepage
              </Link>
            </div>
          ),
        },

        ...(values.homepage?.type === 'checkout'
          ? [
              {
                name: 'homepage.checkout',
                required: true,
                type: 'pageSelect',
                pageTypes: ['checkout', 'event', 'form'],
                label: 'Page',
              },
            ]
          : []),
        ...(values.homepage?.type === 'redirect'
          ? [
              {
                name: 'homepage.redirectUrl',
                required: true,
                type: 'url',
                label: 'Redirect URL',
              },
            ]
          : []),

        {
          name: 'favicon',
          required: false,
          type: 'image',
          label: 'Favicon',
          image: seller.favicon,
          previewSize: 'favicon',
          onUpdate: async image => {
            await updateSeller({ favicon: image });
          },
        },
      ]}
      onSubmit={async values => {
        try {
          const { data } = await updateSeller(values);

          return {
            data,
          };
        } catch (error) {
          return {
            error,
          };
        }
      }}
    />
  );
};

export default StoreForm;
